'use client';
import React from 'react';

import Image from 'next/image';

export default function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden" style={{ backgroundColor: 'var(--background)' }}>
      {/* Background blobs */}
      <div className="absolute top-1/4 right-0 w-96 h-96 blob-primary opacity-60 -z-10 animate-float-slow"></div>
      <div className="absolute bottom-1/4 left-1/4 w-72 h-72 blob-accent opacity-40 -z-10 animate-float-medium"></div>
      <div className="absolute top-1/2 left-0 w-64 h-64 blob-primary opacity-30 -z-10"></div>
      <div className="mx-auto max-w-6xl px-6 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center w-full">
        {/* Left Content */}
        <div className="lg:col-span-6 space-y-8">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ backgroundColor: 'var(--secondary)', border: '1px solid var(--border)' }}>
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: 'var(--primary)' }}></span>
              <span className="relative inline-flex h-2 w-2 rounded-full" style={{ backgroundColor: 'var(--primary)' }}></span>
            </span>
            <span className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--primary)' }}>Same-Day Bookings Available</span>
          </div>

          {/* Heading */}
          <h1 className="text-hero-xl font-display font-bold" style={{ color: 'var(--foreground)' }}>
            Expert physio,{' '}
            <span className="font-display italic text-gradient-teal">delivered</span>{' '}
            to your door
          </h1>

          {/* Description */}
          <p className="text-lg leading-relaxed max-w-lg" style={{ color: 'var(--muted-foreground)' }}>
            Skip the clinic commute. Our certified physiotherapists visit you at home within 24 hours — for injury recovery, post-surgery rehab, sports therapy, stroke recovery, spinal cord injury recovery, and more.
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap gap-4">
            <a
              href="https://wa.me/919497815224?text=Hi%2C%20I%20would%20like%20to%20book%20an%20appointment"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-accent text-sm px-7 py-3.5 inline-flex items-center gap-2">
              Book a Home Visit
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </a>
            <a href="tel:+919497815224" className="btn-secondary text-sm px-7 py-3.5 inline-flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
              </svg>
              Call Now
            </a>
          </div>

          {/* Certifications */}
          <div className="flex flex-wrap items-center gap-4 pt-2">
            {['CMT Certified', 'MIAP Registered', 'KAPC Registered']?.map((cert) =>
            <div key={cert} className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14" style={{ color: 'var(--primary)' }}>
                  <path fillRule="evenodd" d="M8.603 3.799A4.49 4.49 0 0 1 12 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 0 1 3.498 1.307 4.491 4.491 0 0 1 1.307 3.497A4.49 4.49 0 0 1 21.75 12a4.49 4.49 0 0 1-1.549 3.397 4.491 4.491 0 0 1-1.307 3.497 4.491 4.491 0 0 1-3.497 1.307A4.49 4.49 0 0 1 12 21.75a4.49 4.49 0 0 1-3.397-1.549 4.49 4.49 0 0 1-3.498-1.306 4.491 4.491 0 0 1-1.307-3.498A4.49 4.49 0 0 1 2.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 0 1 1.307-3.497 4.49 4.49 0 0 1 3.497-1.307Zm7.007 6.387a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                </svg>
                <span className="font-medium">{cert}</span>
              </div>
            )}
          </div>
        </div>

        {/* Right Images */}
        <div className="lg:col-span-6 relative h-[520px] sm:h-[580px] lg:h-[620px]">
          {/* Image 1 - top right: physiotherapist doing knee assessment at home */}
          <div className="absolute top-0 right-0 w-[58%] aspect-[4/5] rounded-3xl overflow-hidden shadow-teal-lg z-10" style={{ border: '1px solid var(--border)' }}>
            <Image
              alt="Physiotherapist performing home physiotherapy session with patient"
              src="/assets/images/WhatsApp_Image_2026-08-06_at_9.32.18_AM-1785989820166.jpeg"
              fill
              className="object-cover"
              priority
              sizes="(max-width: 768px) 60vw, 30vw" />
          </div>

          {/* Image 2 - middle left: home physio session */}
          <div className="absolute top-[22%] left-0 w-[48%] aspect-square rounded-3xl overflow-hidden shadow-teal-md z-20" style={{ border: '1px solid var(--border)' }}>
            <Image
              alt="Physiotherapist guiding patient through therapeutic exercises during home visit rehabilitation session"
              src="/assets/images/WhatsApp_Image_2026-08-06_at_9.31.46_AM-1785989820190.jpeg"
              fill
              className="object-cover"
              sizes="(max-width: 768px) 50vw, 25vw" />
          </div>

          {/* Image 3 - bottom center: manual therapy at home */}
          <div className="absolute bottom-0 left-[22%] w-[42%] aspect-[3/4] rounded-3xl overflow-hidden shadow-teal-lg z-30" style={{ border: '1px solid var(--border)' }}>
            <Image
              alt="Physiotherapist performing manual therapy on patient's shoulder during home physiotherapy session"
              src="/assets/images/WhatsApp_Image_2026-08-06_at_9.31.08_AM-1785989820464.jpeg"
              fill
              className="object-cover"
              sizes="(max-width: 768px) 45vw, 22vw" />
          </div>
        </div>
      </div>
    </section>);

}